/** Minimal LCS diff for the activity stream's description changes (YouTrack shows red / green markup). */
export interface Op<T> {
  type: "same" | "add" | "del";
  value: T;
}

/** Longest-common-subsequence diff. O(n·m) memory-light DP; callers keep inputs small (lines, or the words of one changed block). */
export function diff<T>(a: T[], b: T[]): Op<T>[] {
  const n = a.length;
  const m = b.length;
  if (n === 0) return b.map((value) => ({ type: "add", value }));
  if (m === 0) return a.map((value) => ({ type: "del", value }));
  // dp[i][j] = LCS length of a[i..] and b[j..]
  const w = m + 1;
  const dp = new Uint32Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i * w + j] = a[i] === b[j] ? dp[(i + 1) * w + j + 1]! + 1 : Math.max(dp[(i + 1) * w + j]!, dp[i * w + j + 1]!);
    }
  }
  const out: Op<T>[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ type: "same", value: a[i]! });
      i++;
      j++;
    } else if (dp[(i + 1) * w + j]! >= dp[i * w + j + 1]!) {
      out.push({ type: "del", value: a[i]! });
      i++;
    } else {
      out.push({ type: "add", value: b[j]! });
      j++;
    }
  }
  while (i < n) out.push({ type: "del", value: a[i++]! });
  while (j < m) out.push({ type: "add", value: b[j++]! });
  return out;
}

/** Words and the whitespace between them, so a re-joined sequence reproduces the text exactly. */
export const words = (s: string): string[] => s.split(/(\s+)/).filter((t) => t.length > 0);

const MAX_CELLS = 4_000_000;

/**
 * Line diff, with the words of adjacent deleted / inserted runs diffed against each other so a changed
 * line shows what changed inside it. Returns one entry per output line; each is a list of marked spans.
 */
export function diffText(before: string, after: string): { type: "same" | "add" | "del" | "mixed"; spans: Op<string>[] }[] {
  const lineOps = diff(before.split("\n"), after.split("\n"));
  const out: { type: "same" | "add" | "del" | "mixed"; spans: Op<string>[] }[] = [];
  let k = 0;
  while (k < lineOps.length) {
    const op = lineOps[k]!;
    if (op.type === "same") {
      out.push({ type: "same", spans: [{ type: "same", value: op.value }] });
      k++;
      continue;
    }
    // A run of deletions followed by a run of insertions is a changed block: diff their words.
    const dels: string[] = [];
    const adds: string[] = [];
    while (k < lineOps.length && lineOps[k]!.type === "del") dels.push(lineOps[k++]!.value);
    while (k < lineOps.length && lineOps[k]!.type === "add") adds.push(lineOps[k++]!.value);
    if (dels.length && adds.length) {
      const wa = words(dels.join("\n"));
      const wb = words(adds.join("\n"));
      const spans = wa.length * wb.length <= MAX_CELLS ? diff(wa, wb) : [...wa.map((value): Op<string> => ({ type: "del", value })), ...wb.map((value): Op<string> => ({ type: "add", value }))];
      // Split the marked words back into lines on the newline tokens.
      let cur: Op<string>[] = [];
      for (const sp of spans) {
        const parts = sp.value.split("\n");
        for (let p = 0; p < parts.length; p++) {
          if (p > 0) {
            out.push({ type: "mixed", spans: cur });
            cur = [];
          }
          if (parts[p]!.length) cur.push({ type: sp.type, value: parts[p]! });
        }
      }
      out.push({ type: "mixed", spans: cur });
    } else {
      for (const value of dels) out.push({ type: "del", spans: [{ type: "del", value }] });
      for (const value of adds) out.push({ type: "add", spans: [{ type: "add", value }] });
    }
  }
  return out;
}
